import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Canonical "$X.XX" cost-per-run formatter.
 *
 * WHY THIS EXISTS: the post-activation card is shared across Pulse (Active/Past)
 * and Team (Keepers). Pulse passed the raw DB `price_per_run` ("0.2") while Team
 * hand-formatted "$0.20" — so the same assistant showed two different prices on
 * two surfaces. There was no single formatter, so the two callers drifted.
 * Route every price string through this helper to keep them in lockstep.
 *
 * Rules:
 * - number            → "$" + n.toFixed(2)            (0.2  → "$0.20")
 * - string with "$"   → passthrough                   ("$0.20" → "$0.20")
 * - numeric string    → "$" + parseFloat(v).toFixed(2) ("0.2" → "$0.20")
 * - null / NaN / junk → "$0.00"
 */
export function formatCostPerRun(value: string | number | null | undefined): string {
  if (value == null) return "$0.00";
  if (typeof value === "number") {
    return Number.isFinite(value) ? `$${value.toFixed(2)}` : "$0.00";
  }
  const trimmed = value.trim();
  if (trimmed.startsWith("$")) return trimmed;
  const num = Number.parseFloat(trimmed);
  return Number.isFinite(num) ? `$${num.toFixed(2)}` : "$0.00";
}
